import { useEffect, useRef, useState } from 'react';
import { DocumentArrowDownIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useExport } from '../hooks/useExport';
import { ExportType } from '../utils/exportUtils';

interface ExportButtonProps {
  reportName: string;
  data: any[];
  filters: any; // ExportFilters type
  className?: string;
  tableDataTransformer?: () => any[];
}

export function ExportButton({
  reportName,
  data,
  filters,
  className = '',
  tableDataTransformer,
}: Readonly<ExportButtonProps>) {
  const [showDropdown, setShowDropdown] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const { isExporting, handleExport } = useExport({ reportName, data, filters, tableDataTransformer });

  const exportOptions = [
    { label: 'Excel (.xlsx) — Table content', format: 'xlsx' as const, includeMetadata: true, exportType: 'table' as ExportType },
    { label: 'Excel (.xlsx) — Data', format: 'xlsx' as const, includeMetadata: true, exportType: 'data' as ExportType },
  ];

  useEffect(() => {
    if (!showDropdown) return;

    const onPointerDown = (e: PointerEvent) => {
      const el = rootRef.current;
      if (el && !el.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowDropdown(false);
      }
    };

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [showDropdown]);

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setShowDropdown(!showDropdown)}
        disabled={isExporting || !data || data.length === 0}
        className="btn-secondary gap-2 disabled:cursor-not-allowed disabled:opacity-50"
        aria-expanded={showDropdown}
        aria-haspopup="menu"
      >
        <DocumentArrowDownIcon className="h-4 w-4 shrink-0" />
        {isExporting ? 'Exporting...' : 'Export'}
        <ChevronDownIcon className="h-4 w-4 shrink-0" />
      </button>

      {showDropdown && (
        <div
          className="absolute right-0 z-20 mt-2 w-56 origin-top-right rounded-lg border border-slate-200/90 bg-white shadow-lg ring-1 ring-slate-900/5 focus:outline-none"
          role="menu"
          aria-label="Export options"
        >
          <div className="py-1">
            <div className="border-b border-slate-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Export Options
            </div>
            {exportOptions.map((option) => (
              <button
                key={option.exportType}
                type="button"
                role="menuitem"
                onClick={() => {
                  handleExport(option.format, option.includeMetadata, option.exportType);
                  setShowDropdown(false);
                }}
                disabled={isExporting}
                className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
